import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ZoneDetailModal = ({ zone, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!zone) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'species', label: 'Species', icon: 'Bird' },
    { id: 'conservation', label: 'Conservation', icon: 'Shield' },
    { id: 'visit', label: 'Visit Info', icon: 'MapPin' }
  ];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? zone?.gallery?.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === zone?.gallery?.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">{zone?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{zone?.category}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-6 p-6">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative h-96 bg-muted rounded-lg overflow-hidden">
                <Image
                  src={zone?.gallery?.[currentImageIndex]?.image}
                  alt={zone?.gallery?.[currentImageIndex]?.imageAlt}
                  className="w-full h-full object-cover"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center animation-organic"
                >
                  <Icon name="ChevronLeft" size={24} color="white" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center animation-organic"
                >
                  <Icon name="ChevronRight" size={24} color="white" />
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-3 py-1 rounded-full">
                  <span className="text-white text-sm">
                    {currentImageIndex + 1} / {zone?.gallery?.length}
                  </span>
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {zone?.gallery?.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 animation-organic ${
                      index === currentImageIndex
                        ? 'border-primary' :'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <Image
                      src={img?.image}
                      alt={img?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Details Panel */}
            <div className="space-y-6">
              {/* Tabs */}
              <div className="flex gap-2 border-b border-border">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2 border-b-2 animation-organic ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span className="text-sm font-medium">{tab?.label}</span>
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="space-y-4">
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Description</h4>
                      <p className="text-sm text-muted-foreground">{zone?.fullDescription}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Key Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {zone?.features?.map((feature, index) => (
                          <span
                            key={index}
                            className="text-xs bg-muted px-3 py-1 rounded-full text-muted-foreground"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-muted p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="Trees" size={16} color="var(--color-secondary)" />
                          <span className="text-xs text-muted-foreground">Species Count</span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">{zone?.speciesCount}</p>
                      </div>
                      <div className="bg-muted p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="MapPin" size={16} color="var(--color-secondary)" />
                          <span className="text-xs text-muted-foreground">Area</span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">{zone?.area}</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'species' && (
                  <div className="space-y-3">
                    {zone?.keySpecies?.map((species, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                        <div className="w-16 h-16 flex-shrink-0 rounded overflow-hidden">
                          <Image
                            src={species?.image}
                            alt={species?.imageAlt}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h5 className="text-sm font-semibold text-foreground">{species?.name}</h5>
                          <p className="text-xs text-muted-foreground italic mb-1">{species?.scientificName}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            species?.status === 'Endangered' ? 'bg-error/20 text-error' :
                            species?.status === 'Vulnerable'? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
                          }`}>
                            {species?.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'conservation' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Conservation Status</h4>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-success"
                            style={{ width: `${zone?.conservationScore}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-foreground">{zone?.conservationScore}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{zone?.conservationStatus}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Active Threats</h4>
                      <div className="space-y-2">
                        {zone?.threats?.map((threat, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5" />
                            <span className="text-sm text-muted-foreground">{threat}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Conservation Efforts</h4>
                      <div className="space-y-2">
                        {zone?.conservationEfforts?.map((effort, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5" />
                            <span className="text-sm text-muted-foreground">{effort}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'visit' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Best Time to Visit</h4>
                      <p className="text-sm text-muted-foreground">{zone?.bestVisitTime}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Access Information</h4>
                      <p className="text-sm text-muted-foreground">{zone?.accessInfo}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Visitor Guidelines</h4>
                      <ul className="space-y-2">
                        {zone?.guidelines?.map((guideline, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Icon name="Info" size={16} color="var(--color-info)" className="mt-0.5" />
                            <span className="text-sm text-muted-foreground">{guideline}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button variant="default" iconName="Calendar" iconPosition="left" fullWidth>
                      Plan Your Visit
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneDetailModal;